/**
 * Blog article — Related content / products section.
 * View options: carousel | marquee (editable titles in preview).
 */
import React from "react";
import { useQuery } from "react-query";
import type { SectionOptions } from "./types";
import {
  useCreatorData,
  useCreatorDataLoading,
  useJournalActivePostSlugOrId,
  findJournalPostBySlugOrId,
  useTemplateSectionInstance,
} from "./templateData";
import { E, useEdit } from "./InlineEdit";
import { Shell, type SectionProps } from "./TemplateSections";
import {
  resolvePostBlogLayout,
  resolveRelatedBlogModules,
} from "./blogRelatedModules";
import {
  BlogRelatedRail,
  mapRelatedProductsToRailItems,
  useRelatedContentRailItems,
  type BlogRelatedView,
} from "./BlogRelatedRail";
import { AffiliateShopLookTemplate } from "./AffiliateShopLookTemplate";
import {
  fetchCollectionProductsForBlog,
  fetchTemplateShopProductsByIds,
} from "./templateShopCatalog";

function RelatedBlockHead({
  kickerPath,
  titlePath,
  kicker,
  title,
}: {
  kickerPath: string;
  titlePath: string;
  kicker: string;
  title: string;
}) {
  const { get } = useEdit();
  const liveKicker = String(get(kickerPath, kicker) || "").trim();
  const liveTitle = String(get(titlePath, title) || "").trim();
  if (!liveKicker && !liveTitle) return null;
  return (
    <div className="mte-tpl-blog-related-head">
      {liveKicker ? (
        <p className="mte-tpl-blog-related-kicker">
          <E path={kickerPath} placeholder="Add eyebrow">
            {kicker}
          </E>
        </p>
      ) : null}
      {liveTitle ? (
        <h2 className="mte-tpl-blog-related-title">
          <E path={titlePath} placeholder="Add section title">
            {title}
          </E>
        </h2>
      ) : null}
    </div>
  );
}

export function RelatedContentSection({ T, cfg }: SectionProps) {
  const sectionPath = useTemplateSectionInstance();
  const CREATOR = useCreatorData();
  const isLoading = useCreatorDataLoading();
  const activeSlug = useJournalActivePostSlugOrId();
  const p = activeSlug
    ? findJournalPostBySlugOrId(CREATOR.journal, activeSlug)
    : CREATOR.journal[0];

  const relatedModules = React.useMemo(
    () =>
      p
        ? resolveRelatedBlogModules(p)
        : { contentPostIds: [], productPostIds: [] },
    [p],
  );
  const postBlogLayout = resolvePostBlogLayout(p);
  // Angela-style shop grid for affiliate roundups; also YT reviews with buy links.
  const isAffiliateRoundup =
    postBlogLayout === "looks_products_roundup" ||
    (postBlogLayout === "transcript_longform_screenshots" &&
      relatedModules.productPostIds.length > 0);

  const userId = CREATOR.bio?.userId || "";
  const username = (CREATOR.bio?.username || "").trim().toLowerCase();
  const viewRaw = String(cfg?.style || "carousel").toLowerCase();
  const view: BlogRelatedView =
    viewRaw === "marquee" ? "marquee" : "carousel";

  const contentHeading =
    String(cfg?.heading ?? "").trim() || "Related content";
  const contentKicker =
    String((cfg?.eyebrow as string) ?? "").trim() || "From this story";
  const productsHeading =
    String((cfg?.productsHeading as string) ?? "").trim() ||
    (isAffiliateRoundup ? "Shop the look" : "Related products");
  const productsKicker =
    String((cfg?.productsEyebrow as string) ?? "").trim() ||
    (isAffiliateRoundup ? "Featured products" : "Shop the look");

  const prefix = sectionPath ? `section.${sectionPath}.` : "section.related.";
  // Titles are edited in-block; strip shell heading so we don't double-render.
  const shellCfg = React.useMemo(() => {
    if (!cfg) return cfg;
    const { heading: _h, eyebrow: _e, ...rest } = cfg;
    return rest;
  }, [cfg]);

  const hasRelatedContentIds = relatedModules.contentPostIds.length > 0;
  const hasRelatedProductIds = relatedModules.productPostIds.length > 0;

  const { items: contentItems, loading: contentLoading } =
    useRelatedContentRailItems({
      userId,
      username,
      postIds: relatedModules.contentPostIds,
      enabled: hasRelatedContentIds,
    });

  const relatedProductsQuery = useQuery(
    [
      "blog-related-products",
      userId,
      username,
      relatedModules.productPostIds.join(","),
    ],
    () =>
      fetchTemplateShopProductsByIds({
        userId,
        username,
        postIds: relatedModules.productPostIds,
        limit: 24,
      }),
    {
      enabled:
        Boolean(userId) &&
        Boolean(username) &&
        relatedModules.productPostIds.length > 0,
    },
  );

  const sourcePostId = relatedModules.contentPostIds[0] || "";
  const collectionProductsQuery = useQuery(
    ["blog-collection-products", username, sourcePostId],
    () =>
      fetchCollectionProductsForBlog({
        username,
        sourcePostId,
      }),
    {
      enabled: Boolean(username) && Boolean(sourcePostId),
    },
  );

  const relatedProducts =
    (relatedProductsQuery.data && relatedProductsQuery.data.length > 0
      ? relatedProductsQuery.data
      : collectionProductsQuery.data) ?? [];
  const productsLoading = Boolean(
    relatedProductsQuery.isLoading ||
      relatedProductsQuery.isFetching ||
      collectionProductsQuery.isLoading ||
      collectionProductsQuery.isFetching,
  );
  const productItems = React.useMemo(
    () => mapRelatedProductsToRailItems(relatedProducts),
    [relatedProducts],
  );

  const showContent =
    hasRelatedContentIds && (contentLoading || contentItems.length > 0);
  const showProducts = productsLoading || productItems.length > 0;

  if (!p) {
    if (isLoading) {
      return (
        <Shell
          T={T}
          cfg={shellCfg}
          independentShellClassName="mte-tpl-blog-related-shell"
        >
          <div className="mte-tpl-blog-related" data-tpl-blog-related="">
            <div className="mte-tpl-blog-rail-skel" aria-busy="true">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="mte-tpl-blog-rail-skel-card" />
              ))}
            </div>
          </div>
        </Shell>
      );
    }
    return null;
  }

  if (!showContent && !showProducts) {
    return null;
  }

  return (
    <Shell
      T={T}
      cfg={shellCfg}
      independentShellClassName={`mte-tpl-blog-related-shell${
        isAffiliateRoundup ? " mte-tpl-blog-related-shell--affiliate" : ""
      }`}
    >
      <div
        className={`mte-tpl-blog-related${
          isAffiliateRoundup ? " mte-tpl-blog-related--affiliate" : ""
        }`}
        data-tpl-blog-related=""
        data-tpl-blog-layout={postBlogLayout || undefined}
      >
        {showProducts && isAffiliateRoundup ? (
          <section
            className="mte-tpl-blog-related-block mte-tpl-blog-related-block--shop-look"
            aria-label="Shop the look"
          >
            <AffiliateShopLookTemplate
              items={productItems}
              userId={userId}
              loading={productsLoading}
              eyebrow={productsKicker}
              heading={productsHeading}
            />
          </section>
        ) : null}
        {showContent ? (
          <section
            className="mte-tpl-blog-related-block"
            aria-label="Related content"
          >
            <RelatedBlockHead
              kickerPath={`${prefix}eyebrow`}
              titlePath={`${prefix}heading`}
              kicker={contentKicker}
              title={contentHeading}
            />
            <BlogRelatedRail
              items={contentItems.slice(0, 1)}
              view={view}
              userId={userId}
              loading={contentLoading}
            />
          </section>
        ) : null}
        {showProducts && !isAffiliateRoundup ? (
          <section
            className="mte-tpl-blog-related-block"
            aria-label="Related products"
          >
            <RelatedBlockHead
              kickerPath={`${prefix}productsEyebrow`}
              titlePath={`${prefix}productsHeading`}
              kicker={productsKicker}
              title={productsHeading}
            />
            <BlogRelatedRail
              items={productItems}
              view={view}
              userId={userId}
              loading={productsLoading}
            />
          </section>
        ) : null}
      </div>
    </Shell>
  );
}

RelatedContentSection.options = {
  style: {
    kind: "radio",
    label: "View",
    options: ["carousel", "marquee"],
    default: "carousel",
  },
  heading: {
    kind: "text",
    label: "Content title",
    default: "Related content",
  },
  eyebrow: {
    kind: "text",
    label: "Content eyebrow",
    default: "From this story",
  },
  productsHeading: {
    kind: "text",
    label: "Products title",
    default: "Related products",
  },
  productsEyebrow: {
    kind: "text",
    label: "Products eyebrow",
    default: "Shop the look",
  },
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface"],
    default: "bg",
  },
} as SectionOptions;
